import { useMutation, useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import {
  AddingOrRemovingRateCardToContractFlowReq,
  ContainerChargeDto,
  ContractUpsertRequestDto,
  CreatingAndUpdatingShippingServiceReq,
  CreatingContractReq,
  DeclarationChargeDto,
  PercentageChargeDto,
  PercentageDiscountDto,
  ResponseDtoContractFlowInfoViewDto,
  ResponseDtoListContractDto,
  ResponseDtoListLegalEntityDto,
  ResponseDtoListServiceProviderDetailsDto,
  ResponseDtoListServiceProviderOverViewDto,
  ResponseDtoPageShippingServiceDto,
  ResponseDtoShippingServiceMetadataDto,
  ShipmentChargeDto,
  ShippingServiceRequestDto,
  WeightChargeDto,
} from 'src/generated/services/TFinancialApi';
import { tFinancialService } from 'src/infra/services/tFinancialService';
import { CacheKeys, cacheKeysUtil } from 'src/utils/cacheKeysUtil';
import { Modality } from '../../types.ts';

export const useGetServiceProvider = (): UseQueryResult<ResponseDtoListServiceProviderDetailsDto> =>
  useQuery(
    cacheKeysUtil.serviceProvider(),
    async (): Promise<ResponseDtoListServiceProviderDetailsDto> => (await tFinancialService.api.getServiceProviderDetails()).data
  );

/**
 * Hook to fetch service provider details specifically for rate cards
 * This uses the dedicated endpoint for rate card-related service provider data
 */
export const useGetServiceProviderForRateCard = (): UseQueryResult<ResponseDtoListServiceProviderDetailsDto> =>
  useQuery(
    cacheKeysUtil.serviceProviderForRateCard(),
    async (): Promise<ResponseDtoListServiceProviderDetailsDto> => (await tFinancialService.api.getServiceProviderDetailsForRateCard()).data
  );

export interface ShippingServiceFilters {
  selectedShippingServiceId?: string[];
  serviceProviderCodes?: string[];
  modalities?: ('ROAD' | 'SEA' | 'RAIL' | 'AIR' | 'COURIER' | 'CT')[];
  shipperCodes?: string[];
  consigneeCodes?: string[];
  billingPartyCodes?: string[];
  routes?: string[];
  services?: string[];
  types?: string[];
  carriers?: string[];
  incoterms?: ('EXW' | 'FCA' | 'FAS' | 'FOB' | 'CPT' | 'CIP' | 'CFR' | 'CIF' | 'DAP' | 'DPU' | 'DDP')[];
  totalLeadTimes?: number[];
  page?: number;
  size?: number;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
}

export const useGetShippingService = (
  filters: ShippingServiceFilters = {},
  options?: UseQueryOptions<ResponseDtoPageShippingServiceDto>
): UseQueryResult<ResponseDtoPageShippingServiceDto> =>
  useQuery(
    cacheKeysUtil.getShippingService(filters),
    async (): Promise<ResponseDtoPageShippingServiceDto> => {
      // Build request data with only non-empty arrays
      const requestData: ShippingServiceRequestDto = {
        paginationRequest: {
          page: filters.page || 0,
          size: filters.size || 100,
          sortingColum: filters.sortBy,
          sortingDirection: filters.sortDirection || 'ASC',
        },
      };

      // Only add filter arrays if they have values
      if (filters.serviceProviderCodes?.length) requestData.serviceProviderCodes = filters.serviceProviderCodes;
      if (filters.modalities?.length) requestData.modalities = filters.modalities;
      if (filters.shipperCodes?.length) requestData.shipperCodes = filters.shipperCodes;
      if (filters.consigneeCodes?.length) requestData.consigneeCodes = filters.consigneeCodes;
      if (filters.billingPartyCodes?.length) requestData.billingPartyCodes = filters.billingPartyCodes;
      if (filters.routes?.length) requestData.routes = filters.routes;
      if (filters.services?.length) requestData.services = filters.services;
      if (filters.types?.length) requestData.types = filters.types;
      if (filters.carriers?.length) requestData.carriers = filters.carriers;
      if (filters.incoterms?.length) requestData.incoterms = filters.incoterms;
      if (filters.totalLeadTimes?.length) requestData.totalLeadTimes = filters.totalLeadTimes;
      if (filters?.selectedShippingServiceId?.length > 0) requestData.priority = { ids: filters.selectedShippingServiceId };

      // Call the new API endpoint
      return (await tFinancialService.api.getShippingService(requestData)).data;
    },
    options
  );

/**
 * Hook to fetch shipping service metadata for filtering options
 * This provides filter options for service providers, modalities, routes, etc.
 */
export const useGetShippingServiceMetadata = (
  filters: ShippingServiceFilters = {},
  options?: UseQueryOptions<ResponseDtoShippingServiceMetadataDto>
): UseQueryResult<ResponseDtoShippingServiceMetadataDto> =>
  useQuery(
    cacheKeysUtil.getShippingServiceMetadata(filters),
    async (): Promise<ResponseDtoShippingServiceMetadataDto> => {
      // Build request data with only non-empty arrays
      const requestData: ShippingServiceRequestDto = {
        // Add required paginationRequest with default values
        paginationRequest: {
          page: 0,
          size: 1000, // Large size to get all metadata
        },
      };

      // Only add filter arrays if they have values
      if (filters.serviceProviderCodes?.length) requestData.serviceProviderCodes = filters.serviceProviderCodes;
      if (filters.modalities?.length) requestData.modalities = filters.modalities;
      if (filters.shipperCodes?.length) requestData.shipperCodes = filters.shipperCodes;
      if (filters.consigneeCodes?.length) requestData.consigneeCodes = filters.consigneeCodes;
      if (filters.billingPartyCodes?.length) requestData.billingPartyCodes = filters.billingPartyCodes;
      if (filters.routes?.length) requestData.routes = filters.routes;
      if (filters.services?.length) requestData.services = filters.services;
      if (filters.types?.length) requestData.types = filters.types;
      if (filters.carriers?.length) requestData.carriers = filters.carriers;
      if (filters.incoterms?.length) requestData.incoterms = filters.incoterms;
      if (filters.totalLeadTimes?.length) requestData.totalLeadTimes = filters.totalLeadTimes;

      // Call the metadata API endpoint
      return (await tFinancialService.api.getShippingServiceMetadata(requestData)).data;
    },
    options
  );

export const useGetAllServiceProviders = (modality?: Modality): UseQueryResult<ResponseDtoListServiceProviderOverViewDto> =>
  useQuery(
    cacheKeysUtil.allServiceProvider(modality),
    async (): Promise<ResponseDtoListServiceProviderOverViewDto> => (await tFinancialService.api.getAllServiceProvider({ modality })).data
  );

export const useGetListBilledEntity = (): UseQueryResult<ResponseDtoListLegalEntityDto> =>
  useQuery(cacheKeysUtil.getListBilledEntity(), async (): Promise<ResponseDtoListLegalEntityDto> => (await tFinancialService.api.getListBilledEntity()).data);

export const useGetContractByShippingService = (
  shippingServiceId: string,
  options?: UseQueryOptions<ResponseDtoListContractDto>
): UseQueryResult<ResponseDtoListContractDto> =>
  useQuery(
    cacheKeysUtil.getContractByShippingService(shippingServiceId),
    async (): Promise<ResponseDtoListContractDto> => {
      const { data } = await tFinancialService.api.getContractByShippingService(shippingServiceId);

      return data;
    },
    {
      ...options,
    }
  );

export const useGetContractFlowByContractIdAndShippingServiceId = (
  contractId: string,
  shippingServiceId: string,
  options?: UseQueryOptions<ResponseDtoContractFlowInfoViewDto>
): UseQueryResult<ResponseDtoContractFlowInfoViewDto> =>
  useQuery(
    cacheKeysUtil.getContractFlowByContractIdAndShippingServiceId(contractId, shippingServiceId),
    async (): Promise<ResponseDtoContractFlowInfoViewDto> => {
      const { data } = await tFinancialService.api.getContractFlowByContractIdAndShippingServiceId(contractId, shippingServiceId);

      return data;
    },
    {
      ...options,
    }
  );

export const useCreateShippingService = () => {
  const queryClient = useQueryClient();

  return useMutation(async (data: CreatingAndUpdatingShippingServiceReq) => tFinancialService.api.createShippingService(data), {
    onSuccess: async (_, args) => {
      await Promise.all([
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.getShippingService],
        }),
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.getShippingServiceMetadata],
        }),
      ]);
    },
  });
};

// Interface for validity period details response
export interface ValidityPeriodDetailResponse {
  data: {
    id: string;
    name: string;
    period: string;
    charges: {
      declaration: number;
      shipment: number;
      weight: number;
      container: number;
      percentage: number;
    };
  };
}

// Real API hook to get validity period details
export const useGetValidityPeriodDetail = (
  validityPeriodId: string | null,
  options?: UseQueryOptions<ValidityPeriodDetailResponse>
): UseQueryResult<ValidityPeriodDetailResponse> =>
  useQuery(
    ['validityPeriodDetail', validityPeriodId],
    async (): Promise<ValidityPeriodDetailResponse> => {
      if (!validityPeriodId) {
        throw new Error('Validity period ID is required');
      }
      // Call the real API endpoint
      const { data } = await tFinancialService.api.getContractByShippingService(validityPeriodId);

      // Find the selected contract
      const selectedContract = data?.data?.find((contract) => contract.id === validityPeriodId) || data?.data?.[0];

      if (!selectedContract) {
        throw new Error('Contract not found');
      }

      // Format the period string
      const period =
        selectedContract.startDate && selectedContract.endDate ? `${selectedContract.startDate} to ${selectedContract.endDate}` : 'Period not specified';

      // Map the API response to our expected format
      return {
        data: {
          id: selectedContract.id || '',
          name: selectedContract.code || 'Validity Period',
          period,
          charges: {
            declaration: selectedContract.chargeCountOverview?.contractDeclarationChargeCount || 0,
            shipment: selectedContract.chargeCountOverview?.contractShipmentChargeCount || 0,
            weight: selectedContract.chargeCountOverview?.contractWeightChargeCount || 0,
            container: selectedContract.chargeCountOverview?.contractContainerChargeCount || 0,
            percentage: selectedContract.chargeCountOverview?.contractPercentageChargeCount || 0,
          },
        },
      };
    },
    {
      enabled: !!validityPeriodId,
      ...options,
    }
  );

export const useUpdateShippingService = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, data }: { id: string; data: CreatingAndUpdatingShippingServiceReq }) => tFinancialService.api.updateShippingService(id, data),
    {
      onSuccess: async (_, args) => {
        await Promise.all([
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.getShippingService],
          }),
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.getShippingServiceMetadata],
          }),
        ]);
      },
    }
  );
};

export const useCreateContract = () => {
  const queryClient = useQueryClient();

  return useMutation(async (data: CreatingContractReq) => tFinancialService.api.createContract(data), {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onSuccess: async (_, args) => {
      await queryClient.invalidateQueries([CacheKeys.getContractByShippingService]);
    },
  });
};

export const useUpdateContract = () => {
  const queryClient = useQueryClient();

  return useMutation(async (data: ContractUpsertRequestDto) => tFinancialService.api.upsertContract(data), {
    onSuccess: async () => {
      await queryClient.invalidateQueries([CacheKeys.getContractByShippingService]);
    },
  });
};

export const useDeleteContract = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, forceDeleteContractFlow }: { id: string; forceDeleteContractFlow: boolean }) =>
      tFinancialService.api.deleteContract(id, { forceDeleteContractFlow }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([CacheKeys.getContractByShippingService]);
      },
    }
  );
};

export const useDeleteContractFlow = () => {
  const queryClient = useQueryClient();

  return useMutation(async ({ id, force }: { id: string; force: boolean }) => tFinancialService.api.deleteContractFlow(id, { force }), {
    onSuccess: async () => {
      await queryClient.invalidateQueries([CacheKeys.getContractByShippingService]);
    },
  });
};

export const useUpsertShipmentCharge = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (data: ShipmentChargeDto & { contractFlowId: string }) => {
      const { contractFlowId, ...chargeData } = data;
      return tFinancialService.api.upsertShipmentCharge(contractFlowId, chargeData as ShipmentChargeDto);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([CacheKeys.getContractFlowByContractIdAndShippingServiceId]);
      },
    }
  );
};

export const useUpsertContainerCharge = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (data: ContainerChargeDto & { contractFlowId: string }) => {
      const { contractFlowId, ...chargeData } = data;
      return tFinancialService.api.upsertContainerCharge(contractFlowId, chargeData as ContainerChargeDto);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([CacheKeys.getContractFlowByContractIdAndShippingServiceId]);
      },
    }
  );
};

export const useUpsertDeclarationCharge = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (data: DeclarationChargeDto & { contractFlowId: string }) => {
      const { contractFlowId, ...chargeData } = data;
      return tFinancialService.api.upsertDeclarationCharge(contractFlowId, chargeData as DeclarationChargeDto);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([CacheKeys.getContractFlowByContractIdAndShippingServiceId]);
      },
    }
  );
};

export const useUpsertPercentageCharge = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (data: PercentageChargeDto & { contractFlowId: string }) => {
      const { contractFlowId, ...chargeData } = data;
      return tFinancialService.api.upsertPercentageCharge(contractFlowId, chargeData as PercentageChargeDto);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([CacheKeys.getContractFlowByContractIdAndShippingServiceId]);
      },
    }
  );
};

export const useUpsertWeightCharge = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (data: WeightChargeDto & { contractFlowId: string }) => {
      const { contractFlowId, ...chargeData } = data;
      return tFinancialService.api.upsertWeightCharge(contractFlowId, chargeData as WeightChargeDto);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([CacheKeys.getContractFlowByContractIdAndShippingServiceId]);
      },
    }
  );
};

export const useRemoveRateCardsToContractFlow = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ contractFlowId, data }: { contractFlowId: string; data: AddingOrRemovingRateCardToContractFlowReq[] }) =>
      tFinancialService.api.removeRateCardsToContractFlow(contractFlowId, data),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([CacheKeys.getContractFlowByContractIdAndShippingServiceId]);
      },
    }
  );
};

export const useAddRateCardsToContractFlow = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ contractFlowId, data }: { contractFlowId: string; data: AddingOrRemovingRateCardToContractFlowReq[] }) =>
      tFinancialService.api.addRateCardsToContractFlow(contractFlowId, data),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([CacheKeys.getContractFlowByContractIdAndShippingServiceId]);
      },
    }
  );
};

export const useDeleteShippingService = () => {
  const queryClient = useQueryClient();

  return useMutation(async (id: string) => tFinancialService.api.deleteShippingService(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([CacheKeys.getShippingService]);
      queryClient.invalidateQueries([CacheKeys.allServiceProvider]);
    },
  });
};

export const useUpsertPercentageDiscount = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (data: PercentageDiscountDto & { contractFlowId: string }) => {
      const { contractFlowId, ...discountData } = data;
      return tFinancialService.api.upsertPercentageDiscount(contractFlowId, discountData as PercentageDiscountDto);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([CacheKeys.getContractFlowByContractIdAndShippingServiceId]);
      },
    }
  );
};

export const useDeletePercentageDiscount = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ contractFlowId, percentageDiscountId }: { contractFlowId: string; percentageDiscountId: string }) =>
      tFinancialService.api.deletePercentageDiscount(contractFlowId, percentageDiscountId),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([CacheKeys.getContractFlowByContractIdAndShippingServiceId]);
      },
    }
  );
};
